import { Injectable, computed, signal } from '@angular/core';

const STORAGE_KEY = 'afs.impersonating';

export interface ImpersonationTarget { id: number; name: string; role: 'Service' | 'Helper'; }

/** The user an Admin is currently acting as (null when not impersonating). Persisted so a
 *  reload or PWA relaunch keeps the target — and the banner that offers Exit. AuthService only
 *  honours it for an Admin session, so a value left on a helper/supervisor device is inert. */
@Injectable({ providedIn: 'root' })
export class ImpersonationService {
  readonly target = signal<ImpersonationTarget | null>(load());
  readonly active = computed(() => this.target() !== null);

  start(t: ImpersonationTarget): void {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(t));
    this.target.set(t);
  }

  stop(): void {
    localStorage.removeItem(STORAGE_KEY);
    this.target.set(null);
  }
}

/** Anything but a well-formed target — including the pre-target flag value '1' — reads as null. */
function load(): ImpersonationTarget | null {
  try {
    const v = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? 'null');
    return v && typeof v.id === 'number' && typeof v.name === 'string'
      && (v.role === 'Service' || v.role === 'Helper')
      ? { id: v.id, name: v.name, role: v.role }
      : null;
  } catch {
    return null;
  }
}
